/**
 * Landings de sub-marca · 3 productos sobre el mismo motor.
 *
 *  · /coordina  → "Coordina"      (saldo común · vertical 14)
 *  · /propina   → "Propina"       (vertical 15)
 *  · /oficina   → "Oficina Fácil" (vertical 16)
 *
 * Cada sub-marca comparte tokens, Wordmark y motor backend, pero con
 * copy, color de acento y casos de uso propios. Misma calidad editorial
 * que HomePage.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';


export type BrandKey = 'coordina' | 'propina' | 'oficina';


interface BrandConfig {
  key: BrandKey;
  eyebrow: string;
  hero: { title: string; highlight: string; rest: string };
  intro: string;
  accent: 'olive' | 'terra' | 'brand';
  steps: { n: string; title: string; body: string }[];
  uses: { n: string; label: string; sub: string }[];
  pricing: string;
  ctaPrimary: string;
  ctaUrl: string;
  trust: { title: string; body: string }[];
  meta: { title: string; description: string };
  parentCopy: string;
}


const BRANDS: Record<BrandKey, BrandConfig> = {
  'coordina': {
    key: 'coordina',
    eyebrow: 'Coordina · saldo común',
    hero: {
      title: 'Una caja común',
      highlight: 'para gastar entre todos.',
      rest: '',
    },
    intro:
      'Cada miembro pone su parte por Bizum o tarjeta. Tú ves el saldo en vivo y apuntas cada gasto del grupo — comidas, hotel, regalos, material — sin Excel ni Bizum manual entre tres. Vale para una peña, una oficina, un viaje o una hermandad.',
    accent: 'olive',
    steps: [
      { n: '01', title: 'Abre la caja', body: 'Pon nombre al grupo, importe sugerido por miembro y comparte el enlace por WhatsApp. 30 segundos.' },
      { n: '02', title: 'Cada uno aporta', body: 'Pegas el enlace en el chat. Cada miembro pone su parte por Bizum o tarjeta cuando quiere.' },
      { n: '03', title: 'Tú apuntas los gastos', body: 'En el panel anotas cada gasto. El saldo se actualiza en vivo · transparente para todo el grupo.' },
    ],
    uses: [
      { n: '01', label: 'Peña deportiva de fin de semana', sub: 'Hotel, comidas, gasolina y entrada al estadio · 14 miembros poniendo 40 €.' },
      { n: '02', label: 'Viaje del cole de los padres', sub: 'Padres viajeros de un grupo escolar · todo lo común sin liarse con quién debe qué.' },
      { n: '03', label: 'Caja de la oficina', sub: 'Café, fruta, regalos de cumple · saldo común sin que nadie adelante.' },
      { n: '04', label: 'Hermandad · gastos del Jueves Santo', sub: 'Flores, túnicas, comida del costalero · cuenta clara para la junta.' },
    ],
    pricing:
      'Gratis crear el grupo y registrar gastos. Solo cobramos un 1 % cuando un miembro paga con Bizum o tarjeta a través nuestro. Si todo es Bizum manual fuera, cuesta 0 €.',
    ctaPrimary: 'Abrir la caja común',
    ctaUrl: '/nuevo?modo=coordina',
    trust: [
      {
        title: 'Sin custodia, sin licencia',
        body: 'El dinero pasa por un procesador licenciado (MONEI, Entidad de Pago con reg. Banco de España #6911) y llega directo al tesorero. Pagafácil no toca fondos · no sustituye a tu gestoría ni asesoría fiscal.',
      },
      {
        title: 'Saldo en vivo · transparente',
        body: 'Todos los miembros pueden ver lo que entra y lo que sale (si tú quieres). Cero "¿pagué yo la última cena?". El tesorero es responsable de las obligaciones legales y contables de la caja común.',
      },
    ],
    meta: {
      title: 'Coordina · saldo común para gastar entre todos',
      description: 'Caja común para peñas, oficinas, viajes, AMPAs y hermandades. Aportes por Bizum o tarjeta, gastos en vivo y transparencia total. Solo cobramos cuando se usa el pago online.',
    },
    parentCopy: 'Una sub-marca de Pagafácil.',
  },

  'propina': {
    key: 'propina',
    eyebrow: 'Propina · QR para tu mesa',
    hero: {
      title: 'La propina,',
      highlight: 'sin cash, sin Bizum manual.',
      rest: '',
    },
    intro:
      'Un QR que vive en la mesa del bar o sobre la barra. El cliente lo escanea, deja una propina con Bizum o tarjeta y se reparte automáticamente entre tu equipo según el turno. Cero suscripción, cero TPV nuevo, cero fricción.',
    accent: 'terra',
    steps: [
      { n: '01', title: 'Crea el bote', body: 'Pon el nombre del local y los miembros del equipo del turno. 30 segundos.' },
      { n: '02', title: 'Imprime el QR', body: 'Descargas el QR estático y lo pegas en mesas, barra o ticket. El mismo QR vale toda la temporada.' },
      { n: '03', title: 'La gente deja propina', body: 'Escanean, eligen importe, pagan. Tú ves quién recibe cuánto en el panel.' },
    ],
    uses: [
      { n: '01', label: 'Chiringuito de playa', sub: 'QR en cada mesa · turno mañana / turno tarde con reparto distinto.' },
      { n: '02', label: 'Bar de barrio', sub: 'Cliente paga la caña por Bizum y deja propina en el mismo flow.' },
      { n: '03', label: 'Hotel · servicio de habitaciones', sub: 'Pegatina del QR en la mesilla · 100 % del importe va al equipo.' },
      { n: '04', label: 'Taxi / VTC', sub: 'QR pegado al asiento · el cliente paga la carrera y deja propina cómoda.' },
    ],
    pricing:
      'Comisión de 1,5 % sobre la propina · el cliente nunca la ve, sale del importe que dejó. Sin fee fijo, sin alta, sin TPV.',
    ctaPrimary: 'Crear bote de propinas',
    ctaUrl: '/nuevo?modo=propina',
    trust: [
      {
        title: 'Las propinas son del equipo',
        body: 'Reparto 100 % al personal del turno. Tu negocio no gestiona el dinero · llega directo a los IBAN de los camareros vía procesador licenciado (MONEI, Banco de España #6911).',
      },
      {
        title: 'Cumplimiento fiscal · responsabilidad del receptor',
        body: 'Generamos un justificante mensual por miembro del equipo. Cada camarero es responsable de declararlo correctamente como rendimiento del trabajo. Pagafácil no sustituye a tu asesoría laboral.',
      },
    ],
    meta: {
      title: 'Propina · QR para propinas en mesa con Bizum o tarjeta',
      description: 'Bote de propinas con QR estático para bares, restaurantes, hoteles y taxis. Pago con Bizum o tarjeta y reparto automático al equipo del turno.',
    },
    parentCopy: 'Una sub-marca de Pagafácil.',
  },

  'oficina': {
    key: 'oficina',
    eyebrow: 'Oficina Fácil · cobro con factura',
    hero: {
      title: 'Cobra a tus clientes',
      highlight: 'con factura, sin Excel.',
      rest: '',
    },
    intro:
      'Para autónomos y microempresas que cobran por Bizum y tienen problemas para emitir factura. Generamos la factura simplificada al pagar el cliente, con tu numeración correlativa, y la guardamos en tu archivo exportable. Conexión con Holded y Quipu en el roadmap.',
    accent: 'brand',
    steps: [
      { n: '01', title: 'Configura tu emisor', body: 'Una vez. NIF, razón social, dirección fiscal, número de factura siguiente. Lo guardamos.' },
      { n: '02', title: 'Manda el cobro al cliente', body: 'WhatsApp o email con el enlace. Cliente paga con Bizum o tarjeta sin descargar nada.' },
      { n: '03', title: 'Factura emitida y guardada', body: 'Al pagar, recibe la factura simplificada en PDF · tú también · numerada y firmada. Lista para Hacienda.' },
    ],
    uses: [
      { n: '01', label: 'Diseñador o desarrollador autónomo', sub: 'Factura el trabajo del mes · cliente paga con Bizum · factura emitida automática.' },
      { n: '02', label: 'Clases particulares y formación', sub: 'Padre paga la mensualidad por Bizum · el alumno se queda con su factura.' },
      { n: '03', label: 'Pequeño comercio sin TPV', sub: 'Compra en el local · QR en la caja · factura simplificada por el cobro.' },
      { n: '04', label: 'Profesional sanitario (fisio, psicólogo, etc.)', sub: 'Sesión cobrada con factura para mutua o IRPF.' },
    ],
    pricing:
      '1 % + 0,30 € por factura emitida · gratis si el cliente paga offline y solo emites la factura para tu archivo. Premium 12 €/mes con facturas ilimitadas y exportación Holded/Quipu.',
    ctaPrimary: 'Probar Oficina Fácil',
    ctaUrl: '/nuevo?modo=oficina-facil',
    trust: [
      {
        title: 'Factura simplificada bien hecha',
        body: 'Cumple Real Decreto 1619/2012 (Reglamento de Facturación). Número correlativo · NIF emisor y receptor (si aplica) · IVA desglosado o nota "exento" según régimen. Tú firmas tu serie y tu numeración.',
      },
      {
        title: 'No sustituye a tu gestoría',
        body: 'Las facturas son auditables y descargables, pero la declaración de IVA, IRPF y cumplimiento Verifactu ante la AEAT es responsabilidad del autónomo. Exportable en cualquier momento a CSV, ZIP de PDFs o vía API a tu programa de contabilidad.',
      },
    ],
    meta: {
      title: 'Oficina Fácil · cobro con factura para autónomos',
      description: 'Cobra a tus clientes por Bizum o tarjeta con factura simplificada automática. Para autónomos, freelances y pequeño comercio. Sin TPV, sin Excel, sin obligaciones.',
    },
    parentCopy: 'Una sub-marca de Pagafácil.',
  },
};


export default function SubBrandLanding({ brand }: { brand: BrandKey }) {
  const cfg = BRANDS[brand];

  useEffect(() => {
    document.title = cfg.meta.title;
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute('name', name);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    setMeta('description', cfg.meta.description);
    setMeta('theme-color', cfg.accent === 'olive' ? '#5E7B3E' : cfg.accent === 'terra' ? '#C2683E' : '#1C5D82');
  }, [cfg]);

  const accentText = cfg.accent === 'olive' ? 'text-olive-700' : cfg.accent === 'terra' ? 'text-terra-700' : 'text-brand';
  const accentBg = cfg.accent === 'olive' ? 'bg-olive' : cfg.accent === 'terra' ? 'bg-terra' : 'bg-brand';
  const accentBgHover = cfg.accent === 'olive' ? 'hover:bg-olive-700' : cfg.accent === 'terra' ? 'hover:bg-terra-700' : 'hover:bg-brand-700';
  const accentBg50 = cfg.accent === 'olive' ? 'bg-olive-50' : cfg.accent === 'terra' ? 'bg-terra-50' : 'bg-brand-50';

  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      {/* Top breadcrumb · vuelta a Pagafácil home */}
      <div className="border-b border-line bg-surface">
        <div className="max-w-6xl mx-auto px-6 py-2 flex items-center gap-4 text-[12px] text-ink-500">
          <Link to="/" className="hover:text-ink font-mono uppercase tracking-[0.12em]">
            ← Pagafácil
          </Link>
          <span className="text-ink-400">·</span>
          <div className="flex items-center gap-3">
            <Link
              to="/coordina"
              className={`hover:text-ink ${brand === 'coordina' ? 'text-ink font-semibold' : ''}`}
            >
              Coordina
            </Link>
            <Link
              to="/propina"
              className={`hover:text-ink ${brand === 'propina' ? 'text-ink font-semibold' : ''}`}
            >
              Propina
            </Link>
            <Link
              to="/oficina"
              className={`hover:text-ink ${brand === 'oficina' ? 'text-ink font-semibold' : ''}`}
            >
              Oficina Fácil
            </Link>
          </div>
        </div>
      </div>

      {/* Header sub-marca */}
      <header className="border-b border-line bg-paper">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Mark accent={cfg.accent} />
            <div>
              <div className="font-display font-bold text-[17px] text-ink tracking-[-0.01em] leading-none">
                {cfg.eyebrow.split(' · ')[0]}
              </div>
              <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-ink-500 mt-0.5">
                · {cfg.parentCopy}
              </div>
            </div>
          </div>
          <Link
            to={cfg.ctaUrl}
            className={`px-4 py-2 rounded-pill text-paper font-semibold text-sm shadow-btn transition ${accentBg} ${accentBgHover}`}
          >
            {cfg.ctaPrimary}
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section>
        <div className="max-w-5xl mx-auto px-6 pt-16 md:pt-24 pb-16">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-4">
            · {cfg.eyebrow}
          </div>
          <h1 className="font-display font-bold text-[44px] sm:text-[54px] lg:text-[64px] leading-[0.98] tracking-[-0.02em] text-ink mb-6 max-w-[820px]">
            {cfg.hero.title}{' '}
            <span className={accentText}>{cfg.hero.highlight}</span>
            {cfg.hero.rest && <> {cfg.hero.rest}</>}
          </h1>
          <p className="text-[17px] leading-relaxed text-ink-700 max-w-[640px] mb-6">
            {cfg.intro}
          </p>
          <div className="inline-flex items-center gap-2 mb-8 px-3 py-1.5 rounded-pill bg-olive-50 border border-[#C7D5A8]">
            <span className="w-1.5 h-1.5 rounded-full bg-olive" />
            <span className="font-mono text-[11.5px] uppercase tracking-[0.14em] text-olive-700">
              Gratis hasta que alguien pague online
            </span>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link
              to={cfg.ctaUrl}
              className={`px-7 py-3.5 rounded-xl text-paper font-semibold text-[15px] shadow-btn transition text-center ${accentBg} ${accentBgHover}`}
            >
              {cfg.ctaPrimary} →
            </Link>
            <a
              href="#como-funciona"
              className="px-7 py-3.5 rounded-xl bg-paper border-[1.5px] border-[#CBD9E2] text-brand font-semibold text-[15px] hover:border-brand transition text-center"
            >
              Cómo funciona
            </a>
          </div>
        </div>
      </section>

      {/* Cómo funciona */}
      <section id="como-funciona" className="bg-paper border-t border-line">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-24">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Cómo funciona
          </div>
          <h2 className="font-display font-bold text-[34px] md:text-[42px] leading-[1.05] tracking-[-0.02em] text-ink mb-12 max-w-[640px]">
            Tres pasos. Ni uno más.
          </h2>
          <ol className="grid md:grid-cols-3 gap-10 md:gap-14">
            {cfg.steps.map((s) => (
              <li key={s.n} className="border-t border-line-strong pt-6">
                <div className={`font-display font-bold text-[44px] leading-none mb-3 ${accentText}`}>
                  {s.n}
                </div>
                <h3 className="font-display font-semibold text-[22px] text-ink mb-2 tracking-[-0.01em]">
                  {s.title}
                </h3>
                <p className="text-[14.5px] text-ink-700 leading-relaxed">{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Casos de uso */}
      <section className="border-t border-line">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-24">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Para qué sirve
          </div>
          <h2 className="font-display font-bold text-[34px] md:text-[42px] leading-[1.05] tracking-[-0.02em] text-ink mb-10 max-w-[700px]">
            Cuatro casos donde lo usan hoy.
          </h2>
          <ul className="divide-y divide-line">
            {cfg.uses.map((u) => (
              <li key={u.n} className="py-5 md:py-6 grid grid-cols-[auto_1fr] gap-5 md:gap-8 items-baseline">
                <span className="font-mono text-[12px] text-ink-500 tracking-[0.1em]">{u.n}</span>
                <div>
                  <h3 className="font-display font-semibold text-[20px] md:text-[24px] text-ink tracking-[-0.01em] mb-1">
                    {u.label}
                  </h3>
                  <p className="text-[14.5px] text-ink-700 leading-relaxed max-w-[640px]">{u.sub}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Confianza + Precio */}
      <section className="bg-albero border-t border-line-strong">
        <div className="max-w-6xl mx-auto px-6 py-20 md:py-24">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Confianza y precio
          </div>
          <div className="grid md:grid-cols-2 gap-6 mb-10">
            {cfg.trust.map((t) => (
              <div key={t.title} className="bg-paper rounded-card border border-line p-7 shadow-card">
                <h3 className="font-display font-semibold text-[20px] text-ink mb-2 tracking-[-0.01em]">
                  {t.title}
                </h3>
                <p className="text-[14.5px] text-ink-700 leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
          <div className={`p-7 rounded-card border ${accentBg50} ${cfg.accent === 'olive' ? 'border-[#C7D5A8]' : cfg.accent === 'terra' ? 'border-[#E6CDB6]' : 'border-[#CBD9E2]'}`}>
            <div className={`font-mono text-[11px] uppercase tracking-[0.18em] mb-2 ${accentText}`}>
              · Precio
            </div>
            <p className="text-[15px] text-ink-700 leading-relaxed">{cfg.pricing}</p>
            <Link
              to="/precio"
              className="inline-block mt-4 font-mono text-[11.5px] uppercase tracking-[0.18em] text-brand hover:text-brand-700"
            >
              · Ver simulador de precio →
            </Link>
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className={`text-paper ${accentBg}`}>
        <div className="max-w-4xl mx-auto px-6 py-20 md:py-24 text-center">
          <h2 className="font-display font-bold text-[36px] md:text-[48px] leading-[1.05] tracking-[-0.02em] mb-5">
            ¿Lo probamos?
          </h2>
          <p className="text-[16px] text-paper/85 mb-9 max-w-[520px] mx-auto">
            Tarda 30 segundos. No pedimos tarjeta. Cuesta 0 € hasta que alguien pague online.
          </p>
          <Link
            to={cfg.ctaUrl}
            className="inline-block px-9 py-4 rounded-xl bg-paper text-ink font-semibold text-[15px] hover:bg-line transition shadow-card"
          >
            {cfg.ctaPrimary}
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}


function Mark({ accent, size = 22 }: { accent: 'olive' | 'terra' | 'brand'; size?: number }) {
  // Mismo wordmark · acento dominante varía por sub-marca.
  const order =
    accent === 'olive'
      ? '#5E7B3E 0 33%, #C2683E 0 66%, #1C5D82 0 100%'
      : accent === 'terra'
      ? '#C2683E 0 33%, #5E7B3E 0 66%, #1C5D82 0 100%'
      : '#1C5D82 0 33%, #C2683E 0 66%, #5E7B3E 0 100%';
  return (
    <div
      className="rounded-full"
      style={{
        width: size,
        height: size,
        background: `conic-gradient(${order})`,
        boxShadow: 'inset 0 0 0 4px #FFFFFF',
      }}
      aria-hidden
    />
  );
}
